import type {ImmersivePlugin, PluginComponent} from './copilot-immersive-plugin'

/**
 * Find the active component for a plugin based on the current location
 */
export function findActiveComponent(
  plugin: ImmersivePlugin | undefined,
  location: {pathname: string; search: string; hash: string},
): PluginComponent | undefined {
  if (!plugin?.components) return undefined

  return plugin.components.find(component =>
    component.routes.some(route => {
      if (route instanceof RegExp) {
        return route.test(location.pathname)
      } else {
        return route(location)
      }
    }),
  )
}

/**
 * Get the active plugin from the current location
 */
export function getActivePluginFromLocation(
  location: {pathname: string; search?: string; hash?: string},
  plugins: ImmersivePlugin[],
): ImmersivePlugin | undefined {
  const {pathname} = location
  if (!pathname) return

  // Convert location to full location object for component route matching
  const fullLocation = {
    pathname,
    search: location.search || '',
    hash: location.hash || '',
  }

  return plugins.find(plugin => {
    if (plugin.components && plugin.components.length > 0) {
      return shouldActivatePlugin(plugin, fullLocation)
    }

    return false
  })
}

/**
 * Determines if a plugin should be activated for the given location.
 *
 * If the plugin has explicit routes defined, those take precedence (backward compatibility).
 * If the plugin only has components with routes, activation is derived from component routes.
 *
 * @param plugin The plugin to check
 * @param location The current location
 * @returns true if the plugin should be activated
 */
export function shouldActivatePlugin(
  plugin: ImmersivePlugin,
  location: {pathname: string; search: string; hash: string},
): boolean {
  // Derive activation from component routes
  if (plugin.components && plugin.components.length > 0) {
    return plugin.components.some(component =>
      component.routes.some(route => {
        if (route instanceof RegExp) {
          return route.test(location.pathname)
        }
        // Function-based route matcher
        return route(location)
      }),
    )
  }

  return false
}
